import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { UserPlus, Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/users`, {
        email: newUserEmail,
        password: newUserPassword,
      });
      toast.success("Notandi búinn til!");
      setNewUserEmail("");
      setNewUserPassword("");
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að búa til notanda");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Ertu viss um að þú viljir eyða þessum notanda?")) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Notanda eytt");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Villa við að eyða notanda");
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl md:text-5xl font-display font-bold tracking-tight" data-testid="users-title">
            Notendastýring
          </h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary hover:bg-primary-600 hover:text-white text-white"
                data-testid="add-user-button"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                Bæta við notanda
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bæta við nýjum notanda</DialogTitle>
                <DialogDescription>
                  Búðu til nýjan admin notanda fyrir kerfið
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddUser} className="space-y-4">
                <div>
                  <Label htmlFor="new-email">Netfang</Label>
                  <Input
                    id="new-email"
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    required
                    placeholder="notandi@example.com"
                    data-testid="new-user-email"
                  />
                </div>
                <div>
                  <Label htmlFor="new-password">Lykilorð</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    data-testid="new-user-password"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-primary-600 hover:text-white text-white"
                  data-testid="create-user-button"
                >
                  {loading ? "Býr til..." : "Búa til notanda"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <h2 className="text-2xl font-heading font-semibold mb-6">Admin notendur</h2>
          {users.length === 0 ? (
            <p className="text-muted-foreground font-body text-center py-8">Engir notendur ennþá</p>
          ) : (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="border border-neutral-200 rounded-lg p-4 flex justify-between items-center"
                  data-testid={`user-${user.id}`}
                >
                  <div>
                    <p className="font-body font-semibold">{user.email}</p>
                    <p className="text-sm text-muted-foreground">
                      Búinn til: {new Date(user.created_at).toLocaleDateString('is-IS')}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteUser(user.id)}
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid={`delete-user-${user.id}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
